// SPDX-License-Identifier: AGPL-3.0-or-later

import type {GuildReadyData} from '@app/features/gateway/types/GatewayGuildTypes';
import type {VoiceState} from '@app/features/gateway/types/GatewayVoiceTypes';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {VoiceStateGatewayHandler} from '@app/features/voice/engine/VoiceStateGatewayHandler';
import {ME} from '@app/vendor/constants/AppConstants';
import {makeAutoObservable, observable, runInAction} from 'mobx';

const logger = new Logger('VoiceStateManager');
class VoiceStateManager {
	private voiceStates = observable.object<Record<string, Record<string, Record<string, VoiceState>>>>({});
	private userVoiceStates = observable.object<Record<string, Record<string, Record<string, VoiceState>>>>({});
	private connectionVoiceStates = observable.object<Record<string, VoiceState>>({});
	private gatewayHandler: VoiceStateGatewayHandler;
	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		this.gatewayHandler = new VoiceStateGatewayHandler(
			() => this.voiceStates,
			() => this.userVoiceStates,
			() => this.connectionVoiceStates,
			this.setVoiceStates.bind(this),
			this.setUserVoiceStates.bind(this),
			this.setConnectionVoiceStates.bind(this),
			this.removeVoiceState.bind(this),
		);
		logger.debug('Initialized');
	}
	private setVoiceStates(states: Record<string, Record<string, Record<string, VoiceState>>>) {
		this.voiceStates = states;
	}
	private setUserVoiceStates(states: Record<string, Record<string, Record<string, VoiceState>>>) {
		this.userVoiceStates = states;
	}
	private setConnectionVoiceStates(states: Record<string, VoiceState>) {
		this.connectionVoiceStates = states;
	}
	handleGatewayVoiceStateUpdate(guildId: string | null, voiceState: VoiceState): void {
		this.gatewayHandler.handleGatewayVoiceStateUpdate(guildId, voiceState);
	}
	handleGatewayVoiceStateDelete(guildId: string, userId: string): void {
		this.gatewayHandler.handleGatewayVoiceStateDelete(guildId, userId);
	}
	handleConnectionOpen(guilds: Array<GuildReadyData>): void {
		this.gatewayHandler.handleConnectionOpen(guilds);
	}
	handleGuildCreate(guild: GuildReadyData): void {
		this.gatewayHandler.handleGuildCreate(guild);
	}
	handleGuildDelete(deletedGuildId: string): void {
		this.gatewayHandler.handleGuildDelete(deletedGuildId);
	}
	getCurrentUserVoiceState(
		guildId?: string | null,
		currentUserId?: string,
		connectionId?: string | null,
	): VoiceState | null {
		const requestedGuildKey = guildId ?? ME;
		if (connectionId) {
			const byConnection = this.connectionVoiceStates[connectionId];
			if (byConnection) {
				if (!guildId || byConnection.guild_id === requestedGuildKey) {
					return byConnection;
				}
			}
		}
		if (!currentUserId) {
			logger.debug('Cannot get current user voice state: no user ID provided');
			return null;
		}
		const userStatesByGuild = this.userVoiceStates[currentUserId];
		if (!userStatesByGuild) {
			return null;
		}
		if (guildId) {
			const guildStates = userStatesByGuild[guildId];
			if (!guildStates) {
				return null;
			}
			const firstConnectionId = Object.keys(guildStates)[0];
			return firstConnectionId ? guildStates[firstConnectionId] : null;
		}
		const guildIds = Object.keys(userStatesByGuild);
		if (guildIds.length === 0) {
			return null;
		}
		const firstGuildStates = userStatesByGuild[guildIds[0]];
		const firstConnectionId = firstGuildStates ? Object.keys(firstGuildStates)[0] : null;
		return firstConnectionId ? firstGuildStates[firstConnectionId] : null;
	}
	getVoiceState(
		guildId: string | null,
		userId?: string,
		currentUserId?: string,
		connectionId?: string | null,
	): VoiceState | null {
		const resolvedUserId = userId ?? currentUserId;
		if (!resolvedUserId) {
			logger.debug('Cannot get voice state: no user ID provided');
			return null;
		}
		const key = guildId ?? ME;
		if (connectionId) {
			const byConnection = this.connectionVoiceStates[connectionId];
			if (byConnection?.user_id === resolvedUserId) {
				if (!guildId || byConnection.guild_id === key) {
					return byConnection;
				}
			}
		}
		const userStatesByGuild = this.userVoiceStates[resolvedUserId];
		if (!userStatesByGuild) {
			return null;
		}
		const guildStates = userStatesByGuild[key];
		if (!guildStates) {
			return null;
		}
		const firstConnectionId = Object.keys(guildStates)[0];
		return firstConnectionId ? guildStates[firstConnectionId] : null;
	}
	getVoiceStateByConnectionId(connectionId: string): VoiceState | null {
		return this.connectionVoiceStates[connectionId] ?? null;
	}
	getConnectionVoiceStates(): Readonly<Record<string, VoiceState>> {
		return this.connectionVoiceStates;
	}
	getAllVoiceStatesInChannel(guildId: string, channelId: string): Readonly<Record<string, VoiceState>> {
		return this.voiceStates[guildId]?.[channelId] ?? {};
	}
	getAllVoiceStatesInGuild(
		guildId: string,
	): Readonly<Record<string, Readonly<Record<string, VoiceState>>>> | undefined {
		return this.voiceStates[guildId];
	}
	getAllVoiceStates(): Readonly<Record<string, Readonly<Record<string, Readonly<Record<string, VoiceState>>>>>> {
		return this.voiceStates;
	}
	private removeVoiceState(connectionId: string, userId: string, guildId: string): void {
		const voiceState = this.connectionVoiceStates[connectionId];
		if (!voiceState) {
			logger.debug('No voice state to remove', {connectionId, userId, guildId});
			return;
		}
		runInAction(() => {
			const newVoiceStates = {...this.voiceStates};
			const newUserVoiceStates = {...this.userVoiceStates};
			const newConnectionVoiceStates = {...this.connectionVoiceStates};
			if (voiceState.channel_id && voiceState.guild_id) {
				const guildStates = {...(newVoiceStates[voiceState.guild_id] || {})};
				const channelStates = {...(guildStates[voiceState.channel_id] || {})};
				delete channelStates[connectionId];
				if (Object.keys(channelStates).length === 0) {
					delete guildStates[voiceState.channel_id];
				} else {
					guildStates[voiceState.channel_id] = channelStates;
				}
				if (Object.keys(guildStates).length === 0) {
					delete newVoiceStates[voiceState.guild_id];
				} else {
					newVoiceStates[voiceState.guild_id] = guildStates;
				}
			}
			const userStates = {...(newUserVoiceStates[userId] || {})};
			const guildStates = {...(userStates[guildId] || {})};
			delete guildStates[connectionId];
			if (Object.keys(guildStates).length === 0) {
				delete userStates[guildId];
			} else {
				userStates[guildId] = guildStates;
			}
			if (Object.keys(userStates).length === 0) {
				delete newUserVoiceStates[userId];
			} else {
				newUserVoiceStates[userId] = userStates;
			}
			delete newConnectionVoiceStates[connectionId];
			this.voiceStates = newVoiceStates;
			this.userVoiceStates = newUserVoiceStates;
			this.connectionVoiceStates = newConnectionVoiceStates;
		});
		logger.debug('Voice state removed', {connectionId, userId, guildId});
	}
	clearAllVoiceStates(): void {
		runInAction(() => {
			this.voiceStates = {};
			this.userVoiceStates = {};
			this.connectionVoiceStates = {};
		});
		logger.info('All voice states cleared');
	}
}
const instance = new VoiceStateManager();
(
	window as typeof window & {
		_voiceStateManager?: VoiceStateManager;
	}
)._voiceStateManager = instance;
export default instance;
